import React, { useRef } from 'react';
import { Download, Printer } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { useLanguage } from '../context/LanguageContext';

const LoksevaArjForm = () => {
  const componentRef = useRef();
  const { language } = useLanguage();

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    const element = componentRef.current;
    const opt = {
      margin: 0, // padding is already handling the margin
      filename: 'Lokseva_Arj.pdf',
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 3, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().from(element).set(opt).save();
  };

  return (
    <div className="min-h-screen bg-gray-200 py-8 font-sans print:py-0 print:bg-white">
      <style>
        {`
          @media print {
            @page {
              size: A4 portrait;
              margin: 0mm;
            }
            body {
              background-color: white !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
        `}
      </style>
      {/* Controls Container - Hidden when printing */}
      <div className="max-w-4xl mx-auto mb-6 flex justify-end space-x-4 print:hidden px-4">
        <button
          onClick={handlePrint}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm transition-colors"
        >
          <Printer className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'प्रिंट करा' : 'Print'}
        </button>
        <button
          onClick={handleDownloadPdf}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 shadow-sm transition-colors"
        >
          <Download className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'पीडीएफ डाउनलोड करा' : 'Download PDF'}
        </button>
      </div>

      {/* A4 Paper Container */}
      <div 
        ref={componentRef}
        className="max-w-[210mm] mx-auto bg-white p-10 shadow-lg text-black print:shadow-none print:max-w-none print:w-[210mm] print:h-[297mm] print:mx-0"
        style={{ minHeight: '297mm' }}
      >
        
        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-lg font-bold border-b border-black inline-block pb-1">
            {language === 'marathi' 
              ? 'महाराष्ट्र लोकसेवा हक्क अध्यादेश -२०१५ अंतर्गत दाखला /प्रमाणपत्र मिळणेकरिता अर्जाचा नमुना'
              : 'Application form for obtaining certificate under Maharashtra Right to Public Services Ordinance-2015'}
          </h1>
        </div>

        {/* Top Info Grid */}
        <div className="grid grid-cols-2 gap-4 mb-6 text-sm leading-relaxed">
          <div>
            <p>{language === 'marathi' ? 'कार्यालयीन अर्ज नोंदणी क्र ..........................' : 'Office Application Registration No ..........................'}</p>
            <p>{language === 'marathi' ? 'दिनांक: .......................................' : 'Date: .......................................'}</p>
            
            <div className="mt-6">
              <p>{language === 'marathi' ? 'प्रति,' : 'To,'}</p>
              <p>{language === 'marathi' ? 'सरपंच / ग्रामपंचायत अधिकारी /निबंधक,' : 'Sarpanch / Gram Panchayat Officer / Registrar,'}</p>
              <p>{language === 'marathi' ? 'ग्रामपंचायत -------------------------' : 'Gram Panchayat -------------------------'}</p>
              <p>{language === 'marathi' ? 'ता.-------------------जि. ---------------------' : 'Taluka------------------- Dist.---------------------'}</p>
            </div>
          </div>
          <div>
            <p className="flex items-end">
              <span className="shrink-0 mr-2">{language === 'marathi' ? 'अर्जदाराचे नाव :-' : 'Applicant\'s Name :-'}</span>
              <span className="border-b border-dashed border-black flex-grow"></span>
            </p>
            <p className="flex items-end mt-2">
              <span className="shrink-0 mr-2">{language === 'marathi' ? 'पत्ता:' : 'Address:'}</span>
              <span className="border-b border-dashed border-black flex-grow"></span>
            </p>
            <p className="flex items-end mt-2">
              <span className="shrink-0 mr-2">{language === 'marathi' ? 'दुरध्वनी क्र.' : 'Phone No.'}</span>
              <span className="border-b border-dashed border-black border-black flex-grow mr-2"></span>,
              <span className="border-b border-dashed border-black flex-grow"></span>
            </p>
          </div>
        </div>

        {/* Subject */}
        <div className="text-center mb-6 font-bold text-[15px]">
          <p>
            {language === 'marathi' ? (
              <>विषय :- महाराष्ट्र लोकसेवा हक्क अध्यादेश -२०१५ अंतर्गत दाखला /प्रमाणपत्र<br/>मिळणेबाबत.</>
            ) : (
              <>Subject :- Regarding obtaining certificate under Maharashtra Right to Public Services Ordinance-2015.</>
            )}
          </p>
        </div>

        {/* Body Text */}
        <div className="mb-4 text-sm leading-relaxed">
          <p>{language === 'marathi' ? 'महोदय,' : 'Respected Sir/Madam,'}</p>
          <p className="indent-8 mt-1">
            {language === 'marathi' 
              ? 'मी खालील स्वाक्षरी करणार वरील पत्त्यावर राहत असून मला खालील दाखल्याची आवश्यकता आहे. :'
              : 'I, the undersigned, residing at the above address, require the following certificate:'}
          </p>
        </div>

        {/* Checkbox List */}
        <div className="space-y-2 mb-8 ml-8 text-sm">
          <div className="flex items-start">
             <div className="w-4 h-4 border border-black mt-1 mr-3 shrink-0"></div>
             <div>{language === 'marathi' 
               ? 'जन्म नोंद दाखला (नाव: --------------------------------------------जन्मदिनांक:--------------------------)'
               : 'Birth Registration Certificate (Name: --------------------------------------------DOB:--------------------------)'}
             </div>
          </div>
          <div className="flex items-start">
             <div className="w-4 h-4 border border-black mt-1 mr-3 shrink-0"></div>
             <div>{language === 'marathi' 
               ? 'मृत्यू नोंद दाखला (नाव : ---------------------------------------------मृत्यू दिनांक: -------------------------)'
               : 'Death Registration Certificate (Name : ---------------------------------------------Date of Death: -------------------------)'}
             </div>
          </div>
          <div className="flex items-start">
             <div className="w-4 h-4 border border-black mt-1 mr-3 shrink-0"></div>
             <div>{language === 'marathi' 
               ? 'विवाह नोंदणी दाखला ( विवाहाचा दिनांक:------------------------------------------ )'
               : 'Marriage Registration Certificate (Date of Marriage:------------------------------------------ )'}
             </div>
          </div>
          <div className="flex items-center">
             <div className="w-4 h-4 border border-black mr-3 shrink-0"></div>
             <span>{language === 'marathi' ? 'दारिद्र्यरेषेखालील असल्याचा दाखला' : 'Below Poverty Line Certificate'}</span>
          </div>
          <div className="flex items-center">
             <div className="w-4 h-4 border border-black mr-3 shrink-0"></div>
             <span>{language === 'marathi' ? 'ग्रामपंचायत येणे बाकी नसल्याचा दाखला' : 'Gram Panchayat No Dues Certificate'}</span>
          </div>
          <div className="flex items-center">
             <div className="w-4 h-4 border border-black mr-3 shrink-0"></div>
             <span>{language === 'marathi' ? 'नमुना नं. ८ चा उतारा (मालमत्ता करआकारणी नोंदवही उतारा ) घर नं.:----------' : 'Form No. 8 Extract (Property Tax Assessment Register Extract) House No.:----------'}</span>
          </div>
          <div className="flex items-center">
             <div className="w-4 h-4 border border-black mr-3 shrink-0"></div>
             <span>{language === 'marathi' ? 'निराधार असले बाबतचा दाखला' : 'Destitute Certificate'}</span>
          </div>
        </div>

        {/* Paragraph */}
        <div className="mb-10 text-sm leading-relaxed">
          <p>
            {language === 'marathi' 
              ? 'मला महाराष्ट्र लोकसेवा हक्क अध्यादेश -२०१५ अंतर्गत दाखला देण्यात यावा. सदरच्या दाखल्याकरिता आवश्यक शुल्क रुपये ------ भरण्यास तयार आहे, तरी दाखला मिळावा ही विनंती.'
              : 'I should be provided with a certificate under the Maharashtra Right to Public Services Ordinance-2015. I am ready to pay the required fee of Rs. ------ for the said certificate. I request you to issue the certificate.'}
          </p>
        </div>

        {/* Middle Footer */}
        <div className="flex justify-between items-start mb-16 text-sm">
          <div>
            <p>{language === 'marathi' ? 'सोबत जोडपत्र :' : 'Enclosures:'}</p>
            <p className="mt-2">१.................................२.................................३.................................................</p>
          </div>
          <div className="text-right">
            <p>{language === 'marathi' ? 'आपला विश्वासू' : 'Yours faithfully,'}</p>
          </div>
        </div>

        {/* Separator Line */}
        <div className="border-b border-dashed border-black mb-6"></div>

        {/* Bottom Receipt Section */}
        <div className="text-center font-bold mb-4">
          <p>{language === 'marathi' ? 'पोहच' : 'Receipt'}</p>
        </div>

        <div className="text-sm leading-relaxed mb-16">
          <p className="leading-loose">
            {language === 'marathi' 
              ? <>श्री./श्रीमती. ----------------------------------------------------------------यांचा ----------------------------- दाखला <br/>मागणी अर्ज दिनांक ----------------------- रोजी प्राप्त झाला.</>
              : <>Shri./Smt. ----------------------------------------------------------------'s ----------------------------- certificate<br/>demand application received on Date -----------------------.</>}
          </p>
        </div>

        {/* Final Signatures */}
        <div className="flex justify-between items-end text-sm mb-12">
          <div>
            <p>{language === 'marathi' ? 'अर्ज नोंदणी क्र. --------' : 'Application Registration No. --------'}</p>
          </div>
          <div className="text-right">
            <p>{language === 'marathi' ? 'पदनिर्देशित अधिकारी' : 'Designated Officer'}</p>
          </div>
        </div>

        {/* Website Link */}
        <div className="text-blue-700 text-xs font-semibold underline">
          www.majhigrampanchayat.com
        </div>

      </div>
    </div>
  );
};

export default LoksevaArjForm;
